import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Check, LogOut, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { listPurchases, markPaid, deletePurchase, deleteAllPurchases, verifyAdminPasscode } from "@/lib/api";

const PasscodeGate = ({ onUnlock }) => {
  const [pc, setPc] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pc) return;
    setLoading(true);
    try {
      await verifyAdminPasscode(pc);
      sessionStorage.setItem("adminPasscode", pc);
      onUnlock();
    } catch {
      toast.error("Incorrect passcode.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="passcode-gate" className="min-h-screen bg-[#F5F5F5] px-6 grid place-items-center">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-[#DDE1E4] rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-[#ECEFF1] grid place-items-center mx-auto">
          <Lock className="w-5 h-5 text-[#546E7A]" strokeWidth={1.5} />
        </div>
        <h1 className="font-serif-display text-3xl text-[#333333] mt-5">Organiser access</h1>
        <p className="text-sm text-[#546E7A] mt-2">Enter the passcode to open the records.</p>
        <input
          data-testid="passcode-input"
          type="password"
          value={pc}
          onChange={(e) => setPc(e.target.value)}
          placeholder="Passcode"
          className="mt-6 w-full text-center bg-transparent border-b border-[#DDE1E4] py-3 text-[#333333] placeholder:text-[#A0AEC0] focus:border-[#C77B7B] focus:outline-none transition-colors"
          autoFocus
        />
        <button
          type="submit"
          data-testid="passcode-submit"
          disabled={loading || !pc}
          className="mt-6 w-full flex items-center justify-center rounded-full bg-[#C77B7B] text-[#F5F5F5] py-3 hover:bg-[#A66666] transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem("adminPasscode"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listPurchases();
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("adminPasscode");
        setUnlocked(false);
        toast.error("Session expired. Please unlock again.");
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unlocked) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  const toggle = async (r) => {
    try {
      const updated = await markPaid(r.reference, !r.paid);
      setRows((prev) => prev.map((x) => (x.reference === r.reference ? updated : x)));
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("adminPasscode");
        setUnlocked(false);
        toast.error("Session expired. Please unlock again.");
      } else {
        toast.error("Could not update");
      }
    }
  };

  const lock = () => {
    sessionStorage.removeItem("adminPasscode");
    setUnlocked(false);
    setRows([]);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the record for ${r.name} (${r.reference})? This cannot be undone.`)) return;
    try {
      await deletePurchase(r.reference);
      setRows((prev) => prev.filter((x) => x.reference !== r.reference));
      toast.success("Record deleted.");
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("adminPasscode");
        setUnlocked(false);
        toast.error("Session expired. Please unlock again.");
      } else {
        toast.error("Could not delete");
      }
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL ${rows.length} records? This is meant for clearing test data before the raffle goes live.`)) return;
    if (!window.confirm("Are you absolutely sure? This permanently removes every record and restarts ticket numbering at #1.")) return;
    try {
      const res = await deleteAllPurchases();
      setRows([]);
      toast.success(`${res.deleted} record${res.deleted === 1 ? "" : "s"} deleted. Ticket numbering restarts at #1.`);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("adminPasscode");
        setUnlocked(false);
        toast.error("Session expired. Please unlock again.");
      } else {
        toast.error("Could not clear records");
      }
    }
  };

  const exportCsv = () => {
    const headers = ["#", "Reference", "Date", "Name", "Email", "Mobile", "Quantity", "Unit price", "Total", "Method", "Card ending", "Status"];
    const csv = [
      headers.join(","),
      ...rows.map((r) => [
        r.purchase_number,
        r.reference,
        r.created_at,
        `"${r.name.replace(/"/g, '""')}"`,
        r.email,
        r.phone,
        r.quantity,
        r.unit_price,
        r.total_amount,
        r.payment_method,
        r.card_last4 || "",
        r.paid ? "received" : "pending",
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `records-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalReceived = rows.reduce((s, r) => s + (r.paid ? r.total_amount : 0), 0);

  if (!unlocked) {
    return <PasscodeGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F5F5F5] px-6 md:px-10 py-14">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C77B7B]">Records</p>
            <h1 className="font-serif-display text-4xl text-[#333333] mt-2">All purchases</h1>
            <p className="text-sm text-[#546E7A] mt-1">
              {rows.length} total · ${totalReceived.toFixed(2)} received
            </p>
          </div>
          <div className="flex gap-2">
            {rows.length > 0 && (
              <button onClick={clearAll} data-testid="clear-all-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#C77B7B]/60 bg-white text-[#C77B7B] hover:bg-[#C77B7B] hover:text-[#F5F5F5] transition-colors text-sm">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Clear all
              </button>
            )}
            <button onClick={exportCsv} data-testid="export-csv-btn" className="px-5 py-2.5 rounded-full border border-[#DDE1E4] bg-white text-[#333333] hover:border-[#C77B7B] transition-colors text-sm">
              Export CSV
            </button>
            <button onClick={load} data-testid="refresh-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#DDE1E4] bg-white text-[#333333] hover:border-[#C77B7B] transition-colors text-sm">
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
            </button>
            <button onClick={lock} data-testid="lock-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#DDE1E4] bg-white text-[#333333] hover:border-[#C77B7B] transition-colors text-sm">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Lock
            </button>
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-white border border-[#DDE1E4] overflow-hidden">
          {loading ? (
            <div className="p-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C77B7B]" /></div>
          ) : rows.length === 0 ? (
            <p className="p-16 text-center text-[#546E7A]">No records yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-[#546E7A] border-b border-[#DDE1E4] bg-[#ECEFF1]">
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Mobile</th>
                    <th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.reference} data-testid={`row-${r.purchase_number}`} className="border-b border-[#EEF1F3] last:border-0 hover:bg-[#FAFBFC]">
                      <td className="px-4 py-3 font-serif-display text-lg text-[#C77B7B]">{r.purchase_number ? `#${r.purchase_number}` : "—"}</td>
                      <td className="px-4 py-3 text-[#546E7A] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 font-medium text-[#333333]">{r.reference}</td>
                      <td className="px-4 py-3">{r.name}</td>
                      <td className="px-4 py-3 text-[#546E7A]">{r.email}</td>
                      <td className="px-4 py-3 text-[#546E7A]">{r.phone}</td>
                      <td className="px-4 py-3">{r.quantity}</td>
                      <td className="px-4 py-3">${r.total_amount.toFixed(2)}</td>
                      <td className="px-4 py-3 capitalize text-[#546E7A]">
                        {r.payment_method}{r.card_last4 ? ` ····${r.card_last4}` : ""}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggle(r)}
                          data-testid={`toggle-paid-${r.reference}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${
                            r.paid ? "bg-[#333333] text-[#F5F5F5]" : "border border-[#DDE1E4] text-[#546E7A] hover:border-[#C77B7B]"
                          }`}
                        >
                          {r.paid && <Check className="w-3 h-3" />} {r.paid ? "Received" : "Mark received"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => remove(r)}
                          data-testid={`delete-${r.reference}`}
                          title="Delete record"
                          className="p-2 rounded-full text-[#A0AEC0] hover:text-[#C77B7B] hover:bg-[#ECEFF1] transition-colors"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
